
import { useState, useCallback, useEffect } from "react";
import { Week, HistoryState, CellPosition } from "../types/workout-types";

export function useWorkoutHistory(
  initialWeeks: Week[],
  initialWeekIndex: number,
  initialDayIndex: number
) {
  const [weeks, setWeeks] = useState<Week[]>(initialWeeks);
  const [currentWeekIndex, setCurrentWeekIndex] = useState(initialWeekIndex);
  const [currentDayIndex, setCurrentDayIndex] = useState(initialDayIndex);
  const [activeCell, setActiveCell] = useState<CellPosition | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [tempValue, setTempValue] = useState("");
  
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [redoStack, setRedoStack] = useState<HistoryState[]>([]);
  const [isUndoRedo, setIsUndoRedo] = useState(false);
  const [forceRender, setForceRender] = useState(0);

  // Initialize history with initial state
  useEffect(() => {
    if (history.length === 0) {
      const initialState: HistoryState = {
        weeks: JSON.parse(JSON.stringify(weeks)),
        currentWeekIndex,
        currentDayIndex,
        activeCell: null,
        editMode: false,
        tempValue: "",
      };
      setHistory([initialState]);
    }
  }, [weeks, currentWeekIndex, currentDayIndex, history.length]);

  // Save current state to history
  const saveToHistory = useCallback(() => {
    if (isUndoRedo) return;

    const currentState: HistoryState = {
      weeks: JSON.parse(JSON.stringify(weeks)),
      currentWeekIndex,
      currentDayIndex,
      activeCell,
      editMode,
      tempValue,
    };

    setHistory((prev) => [...prev, currentState]);
    setRedoStack([]);
  }, [weeks, currentWeekIndex, currentDayIndex, activeCell, editMode, tempValue, isUndoRedo]);

  // Handle undo action
  const handleUndo = useCallback(() => {
    if (history.length <= 1) return; // Keep at least the initial state

    setIsUndoRedo(true);

    // Get the previous state
    const newHistory = [...history];
    const currentState = newHistory.pop();
    const previousState = newHistory[newHistory.length - 1];

    if (newHistory.length > 0) {
      setHistory(newHistory);

      if (currentState) {
        setRedoStack((prev) => [...prev, currentState]);
      }

      if (previousState) {
        setWeeks(previousState.weeks);
        setCurrentWeekIndex(previousState.currentWeekIndex);
        setCurrentDayIndex(previousState.currentDayIndex);
        setActiveCell(previousState.activeCell);
        setEditMode(previousState.editMode);
        setTempValue(previousState.tempValue);

        setForceRender((prev) => prev + 1);
      }
    }

    setTimeout(() => setIsUndoRedo(false), 0);
  }, [history]);

  // Handle redo action
  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;

    setIsUndoRedo(true);

    const newRedoStack = [...redoStack];
    const nextState = newRedoStack.pop();
    setRedoStack(newRedoStack);

    const currentState: HistoryState = {
      weeks: JSON.parse(JSON.stringify(weeks)),
      currentWeekIndex,
      currentDayIndex,
      activeCell,
      editMode,
      tempValue,
    };
    setHistory((prev) => [...prev, currentState]);

    if (nextState) {
      setWeeks(nextState.weeks);
      setCurrentWeekIndex(nextState.currentWeekIndex);
      setCurrentDayIndex(nextState.currentDayIndex);
      setActiveCell(nextState.activeCell);
      setEditMode(nextState.editMode);
      setTempValue(nextState.tempValue);

      setForceRender((prev) => prev + 1);
    }

    setTimeout(() => setIsUndoRedo(false), 0);
  }, [redoStack, weeks, currentWeekIndex, currentDayIndex, activeCell, editMode, tempValue]);

  return {
    weeks,
    setWeeks,
    currentWeekIndex,
    setCurrentWeekIndex,
    currentDayIndex,
    setCurrentDayIndex,
    activeCell,
    setActiveCell,
    editMode,
    setEditMode,
    tempValue,
    setTempValue,
    history,
    redoStack,
    forceRender,
    saveToHistory,
    handleUndo,
    handleRedo,
  };
}
